'use client';

import { PRODUCT_NAME } from '@/constants';
import { useSignInWithCartMerge } from '@/hooks/useSignInWithCartMerge';
import { swal } from '@/lib/sweetalert';
import { useGlobalLoadingAtom } from '@/state';
import { pickErrorMessages } from '@/utils/functions';
import type { Route } from 'next';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { postSignin } from '../api/postSignin';
import { useLoggedInTokenAtom } from '../state';

/**
 * メールログインの hook。
 * postSignin で得たトークンを useSignInWithCartMerge に渡して、ログイン → マージ → 遷移を一元化する。
 */
export const useLogin = (callbackUrl: string) => {
  const router = useRouter();
  const pathname = usePathname();
  const [, setLoggedInToken] = useLoggedInTokenAtom();
  const [isLoading, startGlobalLoading, , forceStopGlobalLoading] = useGlobalLoadingAtom();
  const { signInWithCartMerge } = useSignInWithCartMerge();

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        startGlobalLoading();
        const { result } = await postSignin({ email, password });
        const signInResult = await signInWithCartMerge({ token: result.token });

        if (!signInResult.ok) {
          forceStopGlobalLoading();
          await swal.alert({ text: signInResult.error ?? 'ログインに失敗しました。' });
          return;
        }

        // 既存サーバカートとマージ発生 or マージ恒久失敗 → ユーザにカート状態を確認させるため /cart に誘導
        const shouldShowCart = signInResult.outcome !== 'noMerge';
        const target = (shouldShowCart ? '/cart' : callbackUrl) as Route;
        // 同一 URL への push は router 側でナビゲーション完了が通知されずローディングが残るため明示的に停止
        if (target === pathname) {
          forceStopGlobalLoading();
        } else {
          router.push(target);
        }
        // ログイン Cookie が変わったので、push 先（or 同一 URL の現在ページ）の RSC を破棄して再フェッチさせる
        router.refresh();
        // biome-ignore lint/suspicious/noExplicitAny: APIエラーの型がanyのため
      } catch (error: any) {
        forceStopGlobalLoading();
        if (error.code === 204) {
          setLoggedInToken(error.result.token);
          await swal.alert({
            text: `次の画面で「${PRODUCT_NAME}」に登録してください`,
          });
          router.push(`/auth/join?callbackUrl=${encodeURIComponent(callbackUrl)}`);
          return;
        }
        await swal.messages({ messages: pickErrorMessages(error) });
      }
    },
    [callbackUrl, forceStopGlobalLoading, pathname, router, setLoggedInToken, signInWithCartMerge, startGlobalLoading],
  );

  return { login, isLoading };
};
